import { useSelector } from 'react-redux';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { emptyArray } from 'ui-components/default-props/empty';
import { PrintAll } from '../../../orders/order-details/order-details-nav-bar/print-all';
import {
  ORDER_DETAILS_ACTIONS,
  useTrackOrderDetailsAction,
} from '../../../orders/modals/order-details-modal/event-tracking/use-track-order-details-action';
import Global from '../../../store/global-selectors';

import { CustomsForm } from './components/customs-form';
import { Label } from './components/label';
import { OrderSummary } from './components/order-summary';
import { PackingSlip } from './components/packing-slip';
import { PickList } from './components/pick-list';
import PrintOrderSummaryHotkey from '../../../components/hotkeys/advanced-hotkeys/print-order-summary';
import PrintPackingSlipHotkey from '../../../components/hotkeys/advanced-hotkeys/print-packing-slip';
import PrintPickListHotkey from '../../../components/hotkeys/advanced-hotkeys/print-pick-list';
import { useOrderSummary } from './hooks/use-order-summary';
import { usePackingSlip } from './hooks/use-packing-slip';
import { usePickList } from './hooks/use-pick-list';
import { ShipmentManifest } from '../shipments-grid/components/shipment-manifest';
import { buildShipmentManifestData } from './helper/build-shipment-manifest-data';

export const OrderDetailsPrintDocumentButton = ({ salesOrderId = '', shipmentCount = 1 }) => {
  const userHasShippingRole = useSelector(Global.selectUserhasShippingRole);
  const fulfillmentPlanIds = useSelector(
    (state) =>
      Global.selectEntitiesSalesOrderProperty(state, {
        salesOrderId,
        property: 'fulfillmentPlanIds',
      }) ?? emptyArray,
  );
  const fulfillmentPlans = useSelector(
    (state) =>
      Global.selectEntitiesFulfillmentPlansByIds(state, {
        fulfillmentPlanIds,
      }) ?? emptyArray,
  );
  const fulfillmentIds = useMemo(() => fulfillmentPlans.flatMap((fp) => fp.fulfillmentIds), [fulfillmentPlans]);
  const fulfillments = useSelector((state) => Global.selectEntitiesFulfillmentsByIds(state, { fulfillmentIds }) ?? emptyArray);
  const shipmentManifestData = buildShipmentManifestData(fulfillmentPlans);

  const packingSlipTemplateId = useSelector((state) => {
    const lastFulfillmentPlan = fulfillmentPlans[fulfillmentPlans.length - 1] ?? null;
    return lastFulfillmentPlan !== null
      ? Global.selectEntitiesFulfillmentPlanPackingSlipTemplateId(state, {
          fulfillmentPlanId: lastFulfillmentPlan.fulfillmentPlanId,
        })
      : undefined;
  });
  const { isDisabled: isOrderSummaryHotkeyDisabled, onPrint: orderSummaryHotkeyHandler } = useOrderSummary({ fulfillmentPlanIds });
  const { isDisabled: isPackingSlipHotkeyDisabled, onClick: packingSlipHotkeyHandler } = usePackingSlip({
    fulfillmentPlanIds,
    fulfillmentPlans,
    packingSlipTemplateId,
  });
  const { onClick: pickListHotkeyHandler } = usePickList({
    fulfillmentPlanIds,
  });

  const trackPackingSlipClicks = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.PRINT_PACKING_SLIPS);
  const trackLabelClicks = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.PRINT_LABELS);
  const trackCustomsFormClicks = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.PRINT_CUSTOMS_FORMS);
  const trackPickListClicks = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.PRINT_PICK_LISTS);
  const trackOrderSummaryClicks = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.PRINT_ORDER_SUMMARY);
  const trackShipmentManifestClick = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.PRINT_SHIPMENT_MANIFEST);

  return (
    <React.Fragment>
      {!isOrderSummaryHotkeyDisabled && <PrintOrderSummaryHotkey handler={orderSummaryHotkeyHandler} />}
      {!isPackingSlipHotkeyDisabled && <PrintPackingSlipHotkey handler={packingSlipHotkeyHandler} />}
      <PrintPickListHotkey handler={pickListHotkeyHandler} />
      <PrintAll shipmentCount={shipmentCount}>
        <PackingSlip
          fulfillmentPlanIds={fulfillmentPlanIds}
          fulfillmentPlans={fulfillmentPlans}
          onClick={trackPackingSlipClicks}
          packingSlipTemplateId={packingSlipTemplateId}
        />
        <Label
          fulfillmentIds={fulfillmentIds}
          fulfillments={fulfillments}
          onClick={trackLabelClicks}
          userHasShippingRole={userHasShippingRole}
        />
        <PickList fulfillmentPlanIds={fulfillmentPlanIds} onClick={trackPickListClicks} />
        <CustomsForm fulfillmentIds={fulfillmentIds} fulfillmentPlanIds={fulfillmentPlanIds} onClick={trackCustomsFormClicks} />
        <OrderSummary fulfillmentPlanIds={fulfillmentPlanIds} onClick={trackOrderSummaryClicks} />
        <ShipmentManifest selectedData={shipmentManifestData} trackOnClick={trackShipmentManifestClick} />
      </PrintAll>
    </React.Fragment>
  );
};

OrderDetailsPrintDocumentButton.propTypes = {
  salesOrderId: PropTypes.string,
  shipmentCount: PropTypes.number,
};
